import { createClient, GetContentsQuery } from 'newt-client-js'
import type { News } from '../../types/newt/news'
import type { Column, ColumnCategory } from '../../types/newt/column'
import type { Seminar } from '../../types/newt/seminar'
import type { Service } from '../../types/newt/service'
import { Logo } from '@/types/newt/logo'
import { Logos } from '@/types/newt/logos'
import {
  Departments,
  DocumentTypes,
  Industries,
  Scales,
  Voice
} from '@/types/newt/voice'
import { Department } from '@/types/newt/department'
import { Opportunity } from '@/types/newt/opportunity'
import { Demo, DemoTags } from '@/types/newt/demo'

// import { buildMemoryStorage, setupCache } from 'axios-cache-interceptor'
// import Axios from 'axios'

// const cache = Axios.defaults.cache
//   ? Axios
//   : setupCache(Axios.create(), {
//       storage: buildMemoryStorage(false, 60 * 1000, false),
//     })

export const newtClient = (
  apiType: 'cdn' | 'api' = 'cdn',
  token: string = process.env.NEWT_FRAIM_CDN_TOKEN ?? ''
) =>
  createClient({
    apiType,
    spaceUid: 'fraim',
    token
    // adapter: Axios.getAdapter(cache),
  })

export type NewtClient = ReturnType<typeof newtClient>

// const newtSsrClient = newtClient('cdn')

// 画像最適化用のユーティリティ関数
interface ImageOptimizationOptions {
  width?: number
  quality?: number
  format?: string
}

function getOptimizedImageUrl(
  url: string,
  options: ImageOptimizationOptions = {}
) {
  if (!url) return ''

  if (url.includes('?format=')) return url

  // Newtの画像URLかどうかを確認
  const isNewtImage = url.includes('fraim.assets.newt.so')
  if (!isNewtImage) return url

  // デフォルトのオプション
  const { width = 800, quality = 80, format = 'webp' } = options

  // クエリパラメータを追加
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}format=${format}&width=${width}&quality=${quality}`
}

// HTML内の画像を最適化
function optimizeImagesInHTML(html: string): string {
  if (!html) return ''

  return html.replace(
    /<img([^>]*)src=['"]([^'"]+)['"]([^>]*?)>?/gi,
    (match, beforeAttrs, src, afterAttrs) => {
      if (src.includes('fraim.assets.newt.so')) {
        const optimizedSrc = getOptimizedImageUrl(src)
        const allAttrs = beforeAttrs + afterAttrs
        const hasLoading = /loading=['"][^'"]*['"]/.test(allAttrs)
        const loadingAttr = hasLoading ? '' : ' loading="lazy"'

        return `<img${beforeAttrs} src="${optimizedSrc}"${afterAttrs}${loadingAttr}>`
      }
      return match
    }
  )
}

export const getNewsList = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
}): Promise<{ news: News[]; total: number }> => {
  const { items: news, total } = await client.getContents<News>({
    appUid: 'lawgue',
    modelUid: 'news',
    query: {
      depth: 2,
      ...query
    }
  })
  return {
    news,
    total
  }
}

export const getNewsItem = async ({
  client,
  slug
}: {
  client: NewtClient
  slug: string
}): Promise<News | null> => {
  if (!slug) return null

  const news = await client.getFirstContent<News>({
    appUid: 'lawgue',
    modelUid: 'news',
    query: {
      depth: 2,
      slug
    }
  })
  return news
}

export const getPrevNews = async ({
  client,
  currentNews
}: {
  client: NewtClient
  currentNews: News
}): Promise<{ slug: string } | null> => {
  const { createdAt } = currentNews._sys
  const news = await client.getFirstContent<{ slug: string }>({
    appUid: 'lawgue',
    modelUid: 'news',
    query: {
      select: ['slug'],
      '_sys.createdAt': {
        lt: createdAt
      },
      order: ['-_sys.createdAt']
    }
  })
  return news
}

export const getNextNews = async ({
  client,
  currentNews
}: {
  client: NewtClient
  currentNews: News
}): Promise<{ slug: string } | null> => {
  const { createdAt } = currentNews._sys
  const news = await client.getFirstContent<{ slug: string }>({
    appUid: 'lawgue',
    modelUid: 'news',
    query: {
      select: ['slug'],
      '_sys.createdAt': {
        gt: createdAt
      },
      order: ['_sys.createdAt']
    }
  })
  return news
}

export const getSingle = async (client: NewtClient) => {
  const { items } = await client.getContents<News>({
    appUid: 'lawgue',
    modelUid: 'news',
    query: {
      select: [
        '_id',
        '_sys',
        'title',
        'newsCategory',
        'slug',
        'meta',
        'contents',
        'overwriteCreatedAt',
        'eyecatch'
      ]
    }
  })

  // ニュース記事の画像を最適化
  const optimizedItems = items.map((news) => {
    if (news.eyecatch?.src) {
      news.eyecatch.src = getOptimizedImageUrl(news.eyecatch.src, {
        width: 800,
        quality: 80,
        format: 'webp'
      })
    }

    // コンテンツ内の画像タグを最適化
    if (news.contents && typeof news.contents === 'string') {
      try {
        if (
          news.contents.trim().startsWith('{') ||
          news.contents.trim().startsWith('[')
        ) {
          const parsedContents = JSON.parse(news.contents)

          if (Array.isArray(parsedContents)) {
            const optimizedContents = parsedContents.map((content) => {
              if (content.type === 'RICH_TEXT' && content.data) {
                content.data = content.data.replace(
                  /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                  (match, attrs, src) => {
                    const optimizedSrc = getOptimizedImageUrl(src)
                    return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                  }
                )
              }
              return content
            })

            news.contents = JSON.stringify(optimizedContents)
          } else if (
            typeof parsedContents === 'object' &&
            parsedContents !== null
          ) {
            if (parsedContents.type === 'RICH_TEXT' && parsedContents.data) {
              parsedContents.data = parsedContents.data.replace(
                /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                (match, attrs, src) => {
                  const optimizedSrc = getOptimizedImageUrl(src)
                  return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                }
              )

              news.contents = JSON.stringify(parsedContents)
            }
          }
        } else {
          news.contents = news.contents.replace(
            /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
            (match, attrs, src) => {
              const optimizedSrc = getOptimizedImageUrl(src)
              return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
            }
          )
        }
      } catch (e) {
        console.error('Error optimizing news contents:', e)
      }
    }

    return news
  })

  return optimizedItems
}

// 【コラム】記事一覧を取得
export const getColumn = async ({
  client,
  query,
  includeCategories = false,
  token = '',
  isPreview = false
}: {
  client: NewtClient
  query?: GetContentsQuery
  includeCategories: boolean
  token?: string
  isPreview?: boolean
}): Promise<{
  column: Column[]
  total: number
  categories?: ColumnCategory[]
}> => {
  const { items: column, total } = await client.getContents<Column>({
    appUid: 'lawgue',
    modelUid: 'column',
    query: {
      depth: 2,
      ...query
    }
  })

  let categories
  if (includeCategories) {
    categories = isPreview
      ? await fetchCategoriesDataForPreview(token)
      : await fetchCategoriesData()
  }
  // 画像の最適化
  const optimizedColumns = column.map((item) => {
    if (item.eyecatch?.src) {
      item.eyecatch.src = getOptimizedImageUrl(item.eyecatch.src, {
        width: 800,
        quality: 80,
        format: 'webp'
      })
    }

    return item
  })

  return {
    column: optimizedColumns,
    total,
    categories
  }
}

async function fetchCategoriesData() {
  // カテゴリデータを取得
  const client = newtClient()
  const response = await client.getContents<ColumnCategory[]>({
    appUid: 'lawgue',
    modelUid: 'column-category',
    query: {}
  })
  return response.items
}

// 【コラム】カテゴリデータをプレビュー用に取得
async function fetchCategoriesDataForPreview(token: string) {
  const client = newtClient('api', token)
  const response = await client.getContents<ColumnCategory[]>({
    appUid: 'lawgue',
    modelUid: 'column-category',
    query: {}
  })
  return response.items
}

//【コラム】prevボタン（詳細ページ）
export const getPrevColumn = async (
  currentColumn: Column
): Promise<{ slug: string } | null> => {
  const client = newtClient()
  const { createdAt } = currentColumn._sys
  const column = await client.getFirstContent<{ slug: string }>({
    appUid: 'lawgue',
    modelUid: 'column',
    query: {
      select: ['slug'],
      '_sys.createdAt': {
        lt: createdAt
      },
      order: ['-_sys.createdAt']
    }
  })
  return column
}

//【コラム】nextボタン（詳細ページ）
export const getNextColumn = async (
  currentColumn: Column
): Promise<{ slug: string } | null> => {
  const client = newtClient()
  const { createdAt } = currentColumn._sys
  const column = await client.getFirstContent<{ slug: string }>({
    appUid: 'lawgue',
    modelUid: 'column',
    query: {
      select: ['slug'],
      '_sys.createdAt': {
        gt: createdAt
      },
      order: ['_sys.createdAt']
    }
  })
  return column
}

//【コラム】コラムカテゴリーからスラッグを取得
export const getColumnCategoryBySlug = async (slug: string) => {
  const client = newtClient()
  const res = await client.getFirstContent<ColumnCategory>({
    appUid: 'lawgue',
    modelUid: 'column-category',
    query: {
      slug
    }
  })
  return res
}

//【コラム】カテゴリIDからコラム一覧を取得・表示
export const getColumns = async (columnCategoryId: string) => {
  const client = newtClient()
  const { items } = await client.getContents<Column>({
    appUid: 'lawgue',
    modelUid: 'column',
    query: {
      columnCategory: {
        in: [columnCategoryId]
      }
    }
  })
  return items
}

//【コラム】詳細ページ表示
export const getColumnDetail = async () => {
  const client = newtClient()
  const { items } = await client.getContents<Column>({
    appUid: 'lawgue',
    modelUid: 'column',
    query: {
      select: [
        '_id',
        '_sys',
        'title',
        'columnCategory',
        'slug',
        'meta',
        'contents',
        'overwriteCreatedAt',
        'eyecatch' // アイキャッチ画像がある場合は追加
      ]
    }
  })

  // 画像の最適化
  const optimizedItems = items.map((column) => {
    // アイキャッチ画像の最適化
    if (column.eyecatch?.src) {
      const originalSrc = column.eyecatch.src
      const optimizedSrc = getOptimizedImageUrl(originalSrc, {
        width: 800,
        quality: 80,
        format: 'webp'
      })

      console.log('Column Eyecatch Before:', originalSrc)
      console.log('Column Eyecatch After:', optimizedSrc)

      column.eyecatch.src = optimizedSrc
    }

    // contentsの処理
    if (column.contents && typeof column.contents === 'string') {
      try {
        const parsedContents = JSON.parse(column.contents)

        if (Array.isArray(parsedContents)) {
          const optimizedContents = parsedContents.map((content) => {
            if (content.type === 'RICH_TEXT' && content.data) {
              content.data = content.data.replace(
                /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                (match, attrs, src) => {
                  const optimizedSrc = getOptimizedImageUrl(src)
                  console.log('Rich Text Image Before:', src)
                  console.log('Rich Text Image After:', optimizedSrc)
                  return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                }
              )
            }

            return content
          })

          column.contents = JSON.stringify(optimizedContents)
        }
      } catch (e) {
        console.error('Error parsing column contents:', e)
      }
    }

    return column
  })

  return optimizedItems
}

//【コラム】詳細ページサイドバーの新着コンテンツを最新順に取得
export const getLatestColumns = async () => {
  const client = newtClient()
  const { items } = await client.getContents<Column>({
    appUid: 'lawgue',
    modelUid: 'column',
    query: {
      order: ['-_sys.createdAt'],
      limit: 4,
      depth: 2
    }
  })
  return items
}

//【コラム】関連記事用にコンテンツを取得
export const getRelatedColumns = async (categoryId: string) => {
  const client = newtClient()
  const { items } = await client.getContents<Column>({
    appUid: 'lawgue',
    modelUid: 'column',
    query: {
      order: ['-_sys.createdAt'],
      limit: 5,
      depth: 2,
      columnCategory: categoryId
    }
  })
  return items
}

//【コラム】サイドバーのカテゴリ別コンテンツ合計数取得
export const getColumnCountByCategory = async (category: ColumnCategory) => {
  const client = newtClient()
  const { total } = await client.getContents<ColumnCategory>({
    appUid: 'lawgue',
    modelUid: 'column',
    query: {
      columnCategory: category._id
    }
  })
  return total
}

// 【メイン起業ロゴ】一覧を取得
export const getLogosList = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
}): Promise<{ logos: Logos[]; total: number }> => {
  const { items: logos, total } = await client.getContents<Logos>({
    appUid: 'lawgue',
    modelUid: 'logos',
    query: {
      depth: 2,
      ...query
    }
  })
  // 画像の最適化
  const optimizedMainLogos = logos.map((item) => {
    if (item.logos?.src) {
      item.logos.src = getOptimizedImageUrl(item.logos.src, {
        width: 400,
        quality: 85,
        format: 'webp'
      })
    }

    return item
  })

  return {
    logos: optimizedMainLogos,
    total
  }
}

// 【起業ロゴ】一覧を取得
export const getLogoList = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
}): Promise<{ logo: Logo[]; total: number }> => {
  const { items: logo, total } = await client.getContents<Logo>({
    appUid: 'lawgue',
    modelUid: 'logo',
    query: {
      depth: 2,
      ...query
    }
  })
  // 画像の最適化
  const optimizedLogos = logo.map((item) => {
    if (item.logo?.src) {
      item.logo.src = getOptimizedImageUrl(item.logo.src, {
        width: 400,
        quality: 85,
        format: 'webp'
      })
    }

    return item
  })

  return {
    logo: optimizedLogos,
    total
  }
}

//【セミナー】一覧ページ表示
export const getSeminarList = async ({
  client,
  query,
  token = '',
  isPreview = false
}: {
  client: NewtClient
  query?: GetContentsQuery
  token?: string
  isPreview?: boolean
}): Promise<{ seminar: Seminar[]; total: number }> => {
  const { items: seminar, total } = await client.getContents<Seminar>({
    appUid: 'lawgue',
    modelUid: 'seminar',
    query: {
      depth: 2,
      ...query
    }
  })
  // 画像URL最適化
  const optimizedSeminars = seminar.map((item) => {
    if (item.eyecatch?.src) {
      item.eyecatch.src = getOptimizedImageUrl(item.eyecatch.src, {
        width: 800,
        quality: 80,
        format: 'webp'
      })
    }
    return item
  })
  return {
    seminar: optimizedSeminars,
    total
  }
}

//【セミナー】詳細ページ表示
export const getSeminarDetail = async () => {
  const client = newtClient()
  const { items } = await client.getContents<Seminar>({
    appUid: 'lawgue',
    modelUid: 'seminar',
    query: {
      select: [
        '_id',
        '_sys',
        'title',
        'slug',
        'meta',
        'contents',
        'overwriteCreatedAt',
        'eyecatch'
      ]
    }
  })

  // 画像URLを最適化
  const optimizedItems = items.map((seminar) => {
    if (seminar.eyecatch?.src) {
      const originalSrc = seminar.eyecatch.src
      const optimizedSrc = getOptimizedImageUrl(originalSrc, {
        width: 800,
        quality: 80,
        format: 'webp'
      })
      seminar.eyecatch.src = optimizedSrc
    }

    if (seminar.contents && typeof seminar.contents === 'string') {
      try {
        const parsedContents = JSON.parse(seminar.contents)
        if (Array.isArray(parsedContents)) {
          const optimizedContents = parsedContents.map((content) => {
            // flexboxのサムネイル画像最適化
            if (content.type === 'flexbox' && content.data?.thumbnail?.src) {
              const origThumbSrc = content.data.thumbnail.src
              const optThumbSrc = getOptimizedImageUrl(origThumbSrc)
              console.log('Thumbnail Before:', origThumbSrc)
              console.log('Thumbnail After:', optThumbSrc)
              content.data.thumbnail.src = optThumbSrc
            }

            // リッチテキスト内の画像最適化
            if (content.type === 'RICH_TEXT' && content.data) {
              content.data = content.data.replace(
                /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                (match, attrs, src) => {
                  const optimizedSrc = getOptimizedImageUrl(src)
                  console.log('Rich Text Image Before:', src)
                  console.log('Rich Text Image After:', optimizedSrc)
                  return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                }
              )
            }

            return content
          })
          seminar.contents = JSON.stringify(optimizedContents)
        }
      } catch (e) {
        console.error('Error parsing seminar contents:', e)
      }
    }

    return seminar
  })

  return optimizedItems
}

//【セミナー】prevボタン（詳細ページ）
export const getPrevSeminar = async (
  currentSeminar: Seminar
): Promise<{ slug: string } | null> => {
  const client = newtClient()
  const { createdAt } = currentSeminar._sys
  const seminar = await client.getFirstContent<{ slug: string }>({
    appUid: 'lawgue',
    modelUid: 'seminar',
    query: {
      select: ['slug'],
      '_sys.createdAt': {
        lt: createdAt
      },
      order: ['-_sys.createdAt']
    }
  })
  return seminar
}

//【セミナー】nextボタン（詳細ページ）
export const getNextSeminar = async (
  currentSeminar: Seminar
): Promise<{ slug: string } | null> => {
  const client = newtClient()
  const { createdAt } = currentSeminar._sys
  const seminar = await client.getFirstContent<{ slug: string }>({
    appUid: 'lawgue',
    modelUid: 'seminar',
    query: {
      select: ['slug'],
      '_sys.createdAt': {
        gt: createdAt
      },
      order: ['_sys.createdAt']
    }
  })
  return seminar
}

//【セミナー】その他のセミナー取得（詳細ページ）
export const getRelatedSeminar = async () => {
  const client = newtClient()
  const seminar = await client.getContents<Seminar>({
    appUid: 'lawgue',
    modelUid: 'seminar',
    query: {
      order: ['-_sys.createdAt'],
      limit: 3
    }
  })
  return seminar
}

export const getPage = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
}) => {
  const content = await client.getContents<{
    _id: string
    title: string
    slug: string
    description: string
    content: {
      type: 'MARKDOWN' | 'RICH_TEXT'
      data: string
      raw: { htmlText: string }
    }
    _sys: {
      createdAt: string
      updatedAt: string
    }
  }>({
    appUid: 'lawgue',
    modelUid: 'lawgue-pages',
    query: query ? { slug: query.slug } : undefined
  })
  return content.items[0]
}

//【お役立ち資料】一覧ページ表示
export const getServiceList = async ({
  client,
  query,
  token = ''
}: {
  client: NewtClient
  query?: GetContentsQuery
  token?: string
}): Promise<{ service: Service[]; total: number }> => {
  const { items: service, total } = await client.getContents<Service>({
    appUid: 'lawgue',
    modelUid: 'service',
    query: {
      depth: 2,
      ...query
    }
  })
  // 画像URLを最適化
  const optimizeServices = service.map((item) => {
    if (item.eyecatch?.src) {
      item.eyecatch.src = getOptimizedImageUrl(item.eyecatch.src, {
        width: 800,
        quality: 80,
        format: 'webp'
      })
    }
    return item
  })
  return {
    service: optimizeServices,
    total
  }
}

//【お役立ち資料】詳細ページ表示
export const getServiceDetail = async () => {
  const client = newtClient()
  const { items } = await client.getContents<Service>({
    appUid: 'lawgue',
    modelUid: 'service',
    query: {
      select: [
        '_id',
        '_sys',
        'title',
        'slug',
        'meta',
        'contents',
        'overwriteCreatedAt',
        'eyecatch'
      ]
    }
  })

  // 画像最適化
  const optimizedItems = items.map((service) => {
    // アイキャッチ画像最適化
    if (service.eyecatch?.src) {
      const originalSrc = service.eyecatch.src
      const optimizedSrc = getOptimizedImageUrl(originalSrc, {
        width: 1200,
        quality: 80,
        format: 'webp'
      })

      service.eyecatch.src = optimizedSrc
    }

    // コンテンツ内の画像最適化
    if (service.contents && Array.isArray(service.contents)) {
      service.contents = service.contents.map((content) => {
        // RICH_TEXT内画像を最適化
        if (content.type === 'RICH_TEXT' && content.data) {
          content.data = optimizeImagesInHTML(content.data)
        }

        // IWCコンテンツ内画像最適化
        if (content.type === 'IWC' && content.data) {
          if ((content.data as any).image) {
            ;(content.data as any).image = optimizeImagesInHTML(
              (content.data as any).image
            )
          }
        }

        return content
      })
    }

    return service
  })

  return optimizedItems
}

//【お役立ち資料】prevボタン（詳細ページ）
export const getPrevService = async (
  currentService: Service
): Promise<{ slug: string } | null> => {
  const client = newtClient()
  const { createdAt } = currentService._sys
  const service = await client.getFirstContent<{ slug: string }>({
    appUid: 'lawgue',
    modelUid: 'service',
    query: {
      select: ['slug'],
      '_sys.createdAt': {
        lt: createdAt
      },
      order: ['-_sys.createdAt']
    }
  })
  return service
}

//【お役立ち資料】nextボタン（詳細ページ）
export const getNextService = async (
  currentService: Service
): Promise<{ slug: string } | null> => {
  const client = newtClient()
  const { createdAt } = currentService._sys
  const service = await client.getFirstContent<{ slug: string }>({
    appUid: 'lawgue',
    modelUid: 'service',
    query: {
      select: ['slug'],
      '_sys.createdAt': {
        gt: createdAt
      },
      order: ['_sys.createdAt']
    }
  })
  return service
}

//【お役立ち資料】その他のお役立ち資料取得（詳細ページ）
export const getRelatedService = async () => {
  const client = newtClient()
  const service = await client.getContents<Service>({
    appUid: 'lawgue',
    modelUid: 'service',
    query: {
      order: ['-_sys.createdAt'],
      limit: 2
    }
  })
  return service
}

// 【導入事例】記事一覧を取得
export const getVoice = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
  token?: string
  isPreview?: boolean
}): Promise<{
  voice: Voice[]
  total: number
}> => {
  const { items: voice, total } = await client.getContents<Voice>({
    appUid: 'lawgue',
    modelUid: 'voice',
    query: {
      depth: 2,
      ...query
    }
  })

  // 画像URLを最適化
  const optimizeVoices = voice.map((item) => {
    if (item.eyecatch?.src) {
      item.eyecatch.src = getOptimizedImageUrl(item.eyecatch.src, {
        width: 800,
        quality: 80,
        format: 'webp'
      })
    }
    return item
  })

  return {
    voice,
    total
  }
}

//【導入事例】記事詳細を取得
export const getVoiceSingle = async (client: NewtClient) => {
  const { items } = await client.getContents<Voice>({
    appUid: 'lawgue',
    modelUid: 'voice',
    query: {
      select: [
        '_id',
        '_sys',
        'title',
        'companyImage',
        'slug',
        'meta',
        'contents',
        'overwriteCreatedAt',
        'eyecatch',
        'catchPhrase'
      ]
    }
  })

  const optimizedItems = items.map((voice) => {
    // アイキャッチ画像の最適化
    if (voice.eyecatch?.src) {
      const originalSrc = voice.eyecatch.src
      const optimizedSrc = getOptimizedImageUrl(originalSrc, {
        width: 1200,
        quality: 80,
        format: 'webp'
      })

      // console.log('Voice Eyecatch Before:', originalSrc)
      // console.log('Voice Eyecatch After:', optimizedSrc)

      voice.eyecatch.src = optimizedSrc
    }

    // 企業ロゴ画像最適化
    if (voice.companyImage?.src) {
      const originalSrc = voice.companyImage.src
      const optimizedSrc = getOptimizedImageUrl(originalSrc, {
        width: 150,
        quality: 80,
        format: 'webp'
      })

      // console.log('Company Image Before:', originalSrc)
      // console.log('Company Image After:', optimizedSrc)

      voice.companyImage.src = optimizedSrc
    }

    // コンテンツ内の画像最適化
    if (voice.contents && typeof voice.contents === 'string') {
      try {
        const parsedContents = JSON.parse(voice.contents)

        if (Array.isArray(parsedContents)) {
          const optimizedContents = parsedContents.map((content) => {
            // RICHTEXTの最適化
            if (content.type === 'RICH_TEXT' && content.data) {
              content.data = content.data.replace(
                /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                (match, attrs, src) => {
                  const optimizedSrc = getOptimizedImageUrl(src)
                  console.log('Rich Text Image Before:', src)
                  console.log('Rich Text Image After:', optimizedSrc)
                  return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                }
              )
            }

            // testタイプの場合の最適化
            if (content.type === 'test') {
              if (content.data.assignment) {
                content.data.assignment = content.data.assignment.replace(
                  /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                  (match, attrs, src) => {
                    const optimizedSrc = getOptimizedImageUrl(src)
                    return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                  }
                )
              }
              if (content.data.introduction) {
                content.data.introduction = content.data.introduction.replace(
                  /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                  (match, attrs, src) => {
                    const optimizedSrc = getOptimizedImageUrl(src)
                    return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                  }
                )
              }
              if (content.data.effect) {
                content.data.effect = content.data.effect.replace(
                  /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                  (match, attrs, src) => {
                    const optimizedSrc = getOptimizedImageUrl(src)
                    return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                  }
                )
              }
              if (content.data.point) {
                content.data.point = content.data.point.replace(
                  /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                  (match, attrs, src) => {
                    const optimizedSrc = getOptimizedImageUrl(src)
                    return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                  }
                )
              }
            }

            // faqタイプの場合の最適化
            if (content.type === 'faq' && content.data.items) {
              content.data.items = content.data.items.map((item) => {
                if (item.question) {
                  item.question = item.question.replace(
                    /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                    (match, attrs, src) => {
                      const optimizedSrc = getOptimizedImageUrl(src)
                      return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                    }
                  )
                }
                if (item.answer) {
                  item.answer = item.answer.replace(
                    /<img([^>]*)src=['"](https:\/\/fraim\.assets\.newt\.so\/[^'"]+)['"]/gi,
                    (match, attrs, src) => {
                      const optimizedSrc = getOptimizedImageUrl(src)
                      return `<img${attrs}src="${optimizedSrc}" loading="lazy"`
                    }
                  )
                }
                return item
              })
            }

            return content
          })

          voice.contents = JSON.stringify(optimizedContents)
        }
      } catch (e) {
        console.error('Error parsing voice contents:', e)
      }
    }

    return voice
  })

  return optimizedItems
}

//【導入事例】スラッグから記事詳細を取得
export const getVoiceItem = async ({
  client,
  slug
}: {
  client: NewtClient
  slug: string
}): Promise<Voice | null> => {
  if (!slug) return null
  const voice = await client.getFirstContent<Voice>({
    appUid: 'lawgue',
    modelUid: 'voice',
    query: {
      depth: 2,
      slug
    }
  })
  return voice
}

//【導入事例】関連記事の取得
export const getRelatedVoice = async () => {
  const client = newtClient()
  const voice = await client.getContents<Voice>({
    appUid: 'lawgue',
    modelUid: 'voice',
    query: {
      order: ['-_sys.customOrder'],
      limit: 3
    }
  })
  return voice
}

// 事例-業種を取得
export const getIndustries = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
}): Promise<{ industries: Industries[] }> => {
  const { items: industries } = await client.getContents<Industries>({
    appUid: 'lawgue',
    modelUid: 'industries',
    query: {
      depth: 2,
      ...query
    }
  })
  return {
    industries
  }
}

// 事例-導入部署
export const getDepartments = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
}): Promise<{ departments: Departments[] }> => {
  const { items: departments } = await client.getContents<Departments>({
    appUid: 'lawgue',
    modelUid: 'departments',
    query: {
      depth: 2,
      ...query
    }
  })
  return {
    departments
  }
}

// 事例-企業規模
export const getScales = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
}): Promise<{ scales: Scales[] }> => {
  const { items: scales } = await client.getContents<Scales>({
    appUid: 'lawgue',
    modelUid: 'scales',
    query: {
      depth: 2,
      ...query
    }
  })
  return {
    scales
  }
}

// 事例-文書類型
export const getDocumentTypes = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
}): Promise<{ documentTypes: DocumentTypes[] }> => {
  const { items: documentTypes } = await client.getContents<DocumentTypes>({
    appUid: 'lawgue',
    modelUid: 'documentTypes',
    query: {
      depth: 2,
      ...query
    }
  })
  return {
    documentTypes
  }
}

// 部署区分を取得
export const getDepartment = async ({
  client,
  query,
  token = ''
}: {
  client: NewtClient
  query?: GetContentsQuery
  token?: string
}): Promise<{
  department: Department[]
  total: number
}> => {
  const { items: department, total } = await client.getContents<Department>({
    appUid: 'lawgue',
    modelUid: 'department',
    query: {
      depth: 2,
      order: ['_sys.createdAt'],
      ...query
    }
  })
  return {
    department,
    total
  }
}

// LAWGUEを知ったきっかけ デフォルト値
const DEFAULT_OPPORTUNITY: Opportunity = {
  _id: 'default',
  _sys: {
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    customOrder: 0,
    raw: {
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      firstPublishedAt: new Date().toISOString(),
      publishedAt: new Date().toISOString()
    }
  },
  label: '選択してください。',
  value: ''
}

// LAWGUEを知ったきっかけ オプション取得
export const getOpportunity = async ({
  client,
  query = {}
}: {
  client: NewtClient
  query?: GetContentsQuery
}): Promise<{ options: Opportunity[]; total: number }> => {
  const { items, total } = await client.getContents<Opportunity>({
    appUid: 'lawgue',
    modelUid: 'opportunity',
    query: {
      depth: 2,
      ...query
    }
  })

  const options: Opportunity[] = [DEFAULT_OPPORTUNITY, ...items]

  return {
    options,
    total: total + 1
  }
}

// 【オンラインデモ】デモ一覧を取得
export const getDemo = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
  token?: string
  isPreview?: boolean
}): Promise<{
  demo: Demo[]
  total: number
}> => {
  const { items: demo, total } = await client.getContents<Demo>({
    appUid: 'lawgue',
    modelUid: 'demo',
    query: {
      depth: 2,
      ...query
    }
  })

  // 画像最適化
  const optimizedCDemos = demo.map((item) => {
    if (item.eyecatch?.src) {
      item.eyecatch.src = getOptimizedImageUrl(item.eyecatch.src, {
        width: 800,
        quality: 80,
        format: 'webp'
      })
    }

    return item
  })

  return {
    demo: optimizedCDemos,
    total
  }
}

//【オンラインデモ】デモ詳細を取得
export const getDemoSingle = async (client: NewtClient) => {
  const { items } = await client.getContents<Demo>({
    appUid: 'lawgue',
    modelUid: 'demo',
    query: {
      select: [
        '_id',
        '_sys',
        'title',
        'iframeTag',
        'slug',
        'meta',
        'overwriteCreatedAt'
      ]
    }
  })
  return items
}

//【オンラインデモ】スラッグからデモ詳細を取得
export const getDemoItem = async ({
  client,
  slug
}: {
  client: NewtClient
  slug: string
}): Promise<Demo | null> => {
  if (!slug) return null
  const demo = await client.getFirstContent<Demo>({
    appUid: 'lawgue',
    modelUid: 'demo',
    query: {
      depth: 2,
      slug
    }
  })
  return demo
}

// 【オンラインデモ】タグを取得
export const getDemoTags = async ({
  client,
  query
}: {
  client: NewtClient
  query?: GetContentsQuery
}): Promise<{ demoTags: DemoTags[] }> => {
  const { items: demoTags } = await client.getContents<DemoTags>({
    appUid: 'lawgue',
    modelUid: 'demoTags',
    query: {
      depth: 2,
      ...query
    }
  })
  return {
    demoTags
  }
}
